import { ServiceFavoriteButton } from '../ServiceFavoriteButton/ServiceFavoriteButton.component';
import { Service } from 'pleinchamp-api-client';
import React, { ComponentProps, ComponentPropsWithRef, FC, useEffect, useMemo } from 'react';
import { Trans } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { FeaturedNews } from '@actus/components/FeaturedNews/FeaturedNews.component';
import { ADVERT_FORMAT_ID, ADVERT_PAGE_ID } from '@adverts/business/Adverts.contants';
import { useAdvert } from '@adverts/business/useAdvert';
import { GenericAd } from '@adverts/components/GenericAd.component';
import { Text } from '@components/Text/Text.component';
import { PlcPageHeader } from '@layout/components/PlcPageHeader/PlcPageHeader.component';
import { PlcPageLayout } from '@layout/components/PlcPageLayout/PlcPageLayout.component';
import { PlcDispatch } from '@store/store';
import { Breakpoint, useClassNames, useIsBreakpointDown } from '@styles/breakpoints';
import { userSelectors } from '@user/business/User.store';
import { urlEnum } from '@utils/url';
import classnames from 'classnames';
import { ATLevel2, PlcTagProps } from 'features/tagPlan/business/TagPlan.types';

import './ServiceCommonLayout.scss';

const SERVICE_SLUG_PREFIX = 'services-pleinchamp~';

type Props = ComponentPropsWithRef<'main'> & {
  i18nServiceBreadcrumb?: string;
  i18nServiceTitle?: string;
  stringTitle?: string;
  i18nServiceSubTitle: string;
  news?: ComponentProps<typeof FeaturedNews>['news'];
  isNewsLoading: ComponentProps<typeof FeaturedNews>['isLoading'];
  serviceSlug: Service['slug'];
  withWrapper?: boolean;
  metaTitleI18n?: string;
  metaDescI18n?: string;
};

const ServiceCommonLayout: FC<Props> = ({
  i18nServiceBreadcrumb,
  i18nServiceTitle,
  stringTitle,
  i18nServiceSubTitle,
  className,
  children,
  news = [],
  isNewsLoading,
  serviceSlug,
  withWrapper = true,
  metaTitleI18n,
  metaDescI18n,
  ...rest
}) => {
  useAdvert({
    pageId: ADVERT_PAGE_ID.ServicesPleinchamp,
  });

  const rootClass = classnames('plc-page', className);
  const sectionClassNames = classnames('plc-mb-xl', withWrapper && 'wrapper-s');
  const isSSO = useSelector(userSelectors.isSSO);
  const isSmartphone = useIsBreakpointDown(Breakpoint.s);
  const adClassName = useClassNames({ all: 'plc-mt-m', m: ['plc-mt-0', 'plc-ml-m'] });

  const {
    services: { fetchUserServices },
  } = useDispatch<PlcDispatch>();
  useEffect(() => {
    fetchUserServices();
  }, []);

  const tagProps: PlcTagProps = useMemo(
    () => ({
      isDataLoading: !serviceSlug,
      level2: ATLevel2.Services,
      tagPageNameI18nArray: ['services.tag.title', { code: serviceSlug, notI18n: true }],
    }),
    [serviceSlug]
  );

  return (
    <PlcPageLayout
      className={rootClass}
      metaDescI18n={metaDescI18n || i18nServiceSubTitle}
      metaTitleI18n={metaTitleI18n || i18nServiceTitle || i18nServiceBreadcrumb || 'common.app-name'}
      tagProps={tagProps}
      {...rest}
    >
      <div className={isSmartphone ? '' : 'flex-parent wrapper-s no-padding'}>
        <div className={isSmartphone ? '' : 'flex-column-base'}>
          <PlcPageHeader
            breadcrumbSteps={[
              {
                label: 'common.newspaper-short',
                linkProps: { as: urlEnum.journal, href: urlEnum.journal },
              },
              {
                label: 'common.services.menu-title',
                linkProps: { as: urlEnum.services, href: urlEnum.services },
              },
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              { label: i18nServiceBreadcrumb || i18nServiceTitle || stringTitle! },
            ]}
            className="plc-mb-xl"
            i18nTitle={i18nServiceTitle}
            stringTitle={stringTitle}
            titleClassName="plc-mb-m"
            withBlockAd={false}
          >
            {!isSSO && (
              <ServiceFavoriteButton
                className="plc-mb-basis plc-mb-up-m-l"
                serviceSlug={`${SERVICE_SLUG_PREFIX}${serviceSlug}`}
              />
            )}
            <Text className="plc-container-not-full-width" tag="h2" variant="p">
              <Trans i18nKey={i18nServiceSubTitle} ns={['services', 'treatment-schedule']} />
            </Text>
          </PlcPageHeader>

          <section className={sectionClassNames}>{children}</section>
        </div>

        <div className={isSmartphone ? 'mobile-pub-center plc-mb-basis' : 'grid-pub plc-pt-basis'}>
          <GenericAd className={adClassName} formatId={ADVERT_FORMAT_ID.WideAngle} />
        </div>
      </div>
      <FeaturedNews isLoading={isNewsLoading} news={news} />
    </PlcPageLayout>
  );
};

export { ServiceCommonLayout };
